import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import PWAInstallPrompt from './PWAInstallPrompt.tsx';
import PWAUpdatePrompt from './PWAUpdatePrompt.tsx';
import IOSInstallInstructions from './IOSInstallInstructions.tsx';
import OfflineRestrictionBanner from './OfflineRestrictionBanner.tsx';
import { startBackendMonitoring, stopBackendMonitoring } from '../utils/backendStatus.ts';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext.tsx';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  path: string;
  label: string;
  icon: string;
  submenu?: Array<{
    path: string;
    label: string;
    icon: string;
  }>;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [financialMenuOpen, setFinancialMenuOpen] = useState(false);
  const [investmentMenuOpen, setInvestmentMenuOpen] = useState(false);
  const [isOkrHelpOpen, setIsOkrHelpOpen] = useState(false);
  const { user, logout } = useAuth();
  const { isVisible, toggle } = useBalanceVisibility();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleOffline = () => {
      // Quando ficar offline, o backend também fica offline
      // O monitoramento já cuida disso automaticamente
    };
    
    window.addEventListener('offline', handleOffline);
    
    // Iniciar monitoramento do backend
    startBackendMonitoring(() => {
      // Callback para quando o status do backend mudar
    });
    
    return () => {
      window.removeEventListener('offline', handleOffline);
      stopBackendMonitoring();
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const normalizePath = (path: string) => {
    if (path === '/') return '/';
    return path.replace(/\/+$/, '');
  };

  const isActive = (path: string) => normalizePath(location.pathname) === normalizePath(path);
  const isPrefixActive = (path: string) => {
    const current = normalizePath(location.pathname);
    const target = normalizePath(path);
    return current === target || current.startsWith(`${target}/`);
  };

  // Verificar se algum submenu financeiro está ativo para manter o menu expandido
  const financialPaths = ['/accounts', '/categories', '/transactions', '/reports'];
  const investmentPaths = [
    '/investment-help',
    '/investment-plan',
    '/investment-tracking',
    '/investment-scenarios',
    '/investment-allocation',
    '/investment-goals',
  ];

  const fiiPaths = [
    '/fii/dashboard',
    '/fii/portfolio',
    '/fii/operations',
    '/fii/income',
    '/fii/darf',
    '/fii/faq',
    '/fii/declaracao-bens',
  ];

  const [fiiMenuOpen, setFiiMenuOpen] = useState(false);
  const [importMenuOpen, setImportMenuOpen] = useState(false);

  const importPaths = ['/import', '/import/rules'];

  useEffect(() => {
    if (financialPaths.some(path => isPrefixActive(path))) {
      setFinancialMenuOpen(true);
    }
    if (investmentPaths.some(path => isPrefixActive(path))) {
      setInvestmentMenuOpen(true);
    }
    if (fiiPaths.some(path => isPrefixActive(path))) {
      setFiiMenuOpen(true);
    }
    if (importPaths.some(path => isPrefixActive(path))) {
      setImportMenuOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isFinancialModule = financialPaths.some(path => isPrefixActive(path));
  const isInvestmentModule = investmentPaths.some(path => isPrefixActive(path));
  const isDashboardModule = isPrefixActive('/dashboard');
  const shouldShowBalanceToggle = isFinancialModule || isInvestmentModule || isDashboardModule;

  const menuItems: MenuItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { 
      path: '/financial', 
      label: 'Financeiro', 
      icon: '💼',
      submenu: [
        { path: '/accounts', label: 'Contas', icon: '🏦' },
        { path: '/categories', label: 'Categorias', icon: '📂' },
        { path: '/transactions', label: 'Transações', icon: '💰' },
        { path: '/reports', label: 'Relatórios', icon: '📊' },
      ]
    },
    { path: '/tasks', label: 'Tarefas', icon: '📋' },
    { path: '/objectives', label: 'Metas (OKR)', icon: '🎯' },
    { 
      path: '/investments', 
      label: 'Investimentos', 
      icon: '📈',
      submenu: [
        { path: '/investment-help', label: 'Help', icon: '🆘' },
        { path: '/investment-plan', label: 'Plano', icon: '🧭' },
        { path: '/investment-tracking', label: 'Tracking', icon: '📒' },
        { path: '/investment-scenarios', label: 'Cenários', icon: '🔍' },
        { path: '/investment-allocation', label: 'Alocação', icon: '🧩' },
        { path: '/investment-goals', label: 'Metas', icon: '🎯' },
      ]
    },
    {
      path: '/fii',
      label: 'FIIs',
      icon: '🏢',
      submenu: [
        { path: '/fii/dashboard', label: 'Dashboard FII', icon: '📈' },
        { path: '/fii/portfolio', label: 'Carteira FII', icon: '📊' },
        { path: '/fii/operations', label: 'Operações', icon: '🔄' },
        { path: '/fii/income', label: 'Rendimentos', icon: '💰' },
        { path: '/fii/darf', label: 'Controle DARF', icon: '🧾' },
        { path: '/fii/declaracao-bens', label: 'Declaração de Bens', icon: '📋' },
        { path: '/fii/faq', label: 'FAQ', icon: '❓' },
      ]
    },
    {
      path: '/import',
      label: 'Importar Extrato',
      icon: '📥',
      submenu: [
        { path: '/import', label: 'Importar', icon: '📁' },
        { path: '/import/rules', label: 'Regras', icon: '⚙️' },
      ]
    },
    { path: '/settings', label: 'Configurações', icon: '⚙️' },
  ];

  const hasActiveSubmenu = (item: MenuItem) => {
    if (!item.submenu) return false;
    return item.submenu.some(subItem => isPrefixActive(subItem.path));
  };

  const isOkrModule = isPrefixActive('/objectives') || isPrefixActive('/key-results');

  return (
    <div className="layout">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <img 
              src="/logo.png"
              alt="Prumo" 
              className="sidebar-logo"
              onError={(e) => {
                console.error('Erro ao carregar logo:', e);
                console.error('Tentou carregar de: /logo.png');
                const target = e.target as HTMLImageElement;
                if (target) {
                  target.style.display = 'none';
                }
              }}
              onLoad={() => {
                console.log('Logo carregado com sucesso');
              }}
            />
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(false)}
          >
            ✕
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            if (item.submenu) {
              const isInvestmentMenu = item.path === '/investments';
              const isFIIMenu = item.path === '/fii';
              const isImportMenu = item.path === '/import';
              const isExpanded = isInvestmentMenu
                ? investmentMenuOpen || hasActiveSubmenu(item)
                : isFIIMenu
                ? fiiMenuOpen || hasActiveSubmenu(item)
                : isImportMenu
                ? importMenuOpen || hasActiveSubmenu(item)
                : financialMenuOpen || hasActiveSubmenu(item);
              return (
                <div key={item.path} className="nav-group">
                  <button
                    className={`nav-item nav-group-header ${hasActiveSubmenu(item) ? 'active' : ''}`}
                    onClick={() => {
                      if (isInvestmentMenu) {
                        setInvestmentMenuOpen(!investmentMenuOpen);
                      } else if (isFIIMenu) {
                        setFiiMenuOpen(!fiiMenuOpen);
                      } else if (isImportMenu) {
                        setImportMenuOpen(!importMenuOpen);
                      } else {
                        setFinancialMenuOpen(!financialMenuOpen);
                      }
                    }}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                    <span className={`nav-arrow ${isExpanded ? 'expanded' : ''}`}>▼</span>
                  </button>
                  {isExpanded && (
                    <div className="nav-submenu">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`nav-item nav-subitem ${isActive(subItem.path) ? 'active' : ''}`}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <span className="nav-icon">{subItem.icon}</span>
                          <span className="nav-label">{subItem.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <Link 
            to="/profile" 
            className="user-info-link"
            onClick={() => setSidebarOpen(false)}
          >
            <div className="user-info">
              <div className="user-avatar">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="user-details">
                <div className="user-name">{user?.name}</div>
                <div className="user-email">{user?.email}</div>
              </div>
            </div>
          </Link>
          
          <button className="logout-button" onClick={handleLogout}>
            Sair
          </button>
        </div>
      </div>

      {/* Overlay para mobile */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="main-content">
        <header className="main-header">
          <button 
            className="menu-toggle"
            onClick={() => setSidebarOpen(true)}
          >
            ☰
          </button>
          <div className="main-title-group">
            <h1 className="page-title">
              {(() => {
                // Buscar o título da página atual
                for (const item of menuItems) {
                  if (item.submenu) {
                    const activeSubItem = item.submenu.find(subItem => isActive(subItem.path));
                    if (activeSubItem) return activeSubItem.label;
                  } else if (isPrefixActive(item.path)) {
                    return item.label;
                  }
                }
                if (isPrefixActive('/key-results')) {
                  return 'Metas (OKR)';
                }
                return 'Prumo';
              })()}
            </h1>
            {isOkrModule && (
              <button
                className="main-help-button"
                onClick={() => setIsOkrHelpOpen(true)}
                title="Ajuda sobre OKRs"
              >
                ?
              </button>
            )}
          </div>
          {shouldShowBalanceToggle && (
            <span 
              className="balance-visibility-toggle"
              onClick={toggle}
              title={isVisible ? 'Ocultar saldos' : 'Mostrar saldos'}
              aria-label={isVisible ? 'Ocultar saldos' : 'Mostrar saldos'}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle();
                }
              }}
            >
              {isVisible ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4C7 4 2.73 7.11 1 11.5C2.73 15.89 7 19 12 19C17 19 21.27 15.89 23 11.5C21.27 7.11 17 4 12 4Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <circle cx="12" cy="11.5" r="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <circle cx="12" cy="11.5" r="1.5" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4C7 4 2.73 7.11 1 11.5C2.73 15.89 7 19 12 19C17 19 21.27 15.89 23 11.5C21.27 7.11 17 4 12 4Z" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.5"/>
                  <circle cx="12" cy="11.5" r="3" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.5"/>
                  <circle cx="12" cy="11.5" r="1.5" fill="currentColor" opacity="0.5"/>
                  <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              )}
            </span>
          )}
        </header>

        <OfflineRestrictionBanner />

        <main className="content">
          {children}
        </main>

        {isOkrHelpOpen && (
          <div className="main-help-overlay" onClick={() => setIsOkrHelpOpen(false)}>
            <div className="main-help-modal" onClick={(event) => event.stopPropagation()}>
              <button className="main-help-close" type="button" onClick={() => setIsOkrHelpOpen(false)}>
                ✕
              </button>
              <h3>O que são OKRs?</h3>
              <p>
                OKRs são metas com resultados mensuráveis. Você define um objetivo claro e acompanha 2 a 5 resultados
                que mostram se está avançando.
              </p>
              <h4>Como preencher um objetivo</h4>
              <ul>
                <li>Defina o período (anual ou trimestral).</li>
                <li>Escreva um objetivo qualitativo e direto.</li>
                <li>Use datas de início e fim para delimitar o ciclo.</li>
              </ul>
              <h4>Exemplos de objetivos reais</h4>
              <ul>
                <li>“Reduzir gastos fixos e aumentar a taxa de poupança”.</li>
                <li>“Melhorar minha saúde física até o fim do trimestre”.</li>
                <li>“Organizar a vida financeira para criar reserva”.</li>
              </ul>
              <h4>Como preencher Key Results</h4>
              <ul>
                <li>Use números: percentual, quantidade ou valor.</li>
                <li>Seja específico: “correr 60 km no trimestre”.</li>
                <li>Evite mais de 5 resultados para manter foco.</li>
              </ul>
              <h4>Exemplos de Key Results reais</h4>
              <ul>
                <li>“Reduzir gastos fixos de R$ 3.200 para R$ 2.600 até 31/03”.</li>
                <li>“Guardar R$ 6.000 no trimestre”.</li>
                <li>“Correr 80 km no trimestre”.</li>
                <li>“Eliminar 2 assinaturas não usadas até 15/02”.</li>
              </ul>
              <h4>Exemplos de indicadores mensais</h4>
              <ul>
                <li>Janeiro: gastos fixos R$ 2.850; poupança R$ 1.500.</li>
                <li>Fevereiro: gastos fixos R$ 2.700; poupança R$ 2.000.</li>
                <li>Março: gastos fixos R$ 2.600; poupança R$ 2.500.</li>
              </ul>
            </div>
          </div>
        )}
      </div>
      
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
      
      {/* PWA Update Prompt */}
      <PWAUpdatePrompt />
      
      {/* iOS Install Instructions */}
      <IOSInstallInstructions />
    </div>
  );
};

export default Layout;